/* ============================================================
   androidDumpKit.js — alat robot telefon untuk app yang skrinnya
   BOLEH DIBACA (uiautomator dump): TikTok, Facebook.
   ============================================================
   Berbeza dengan enjin Shopee (androidRunner.js — skrin post Shopee tak
   boleh dibaca → koordinat + vision + kalibrasi), app di sini dipandu
   ikut TEKS / content-desc / resource-id yang dibaca terus dari skrin.
   Tiada kalibrasi, tiada AI. Diukur pada phone sebenar 4 Nov 2026
   (PLAN_PHONE_TIKTOK_FB.md).

   Prinsip:
   - Setiap langkah MESTI disahkan dengan bacaan skrin (bukan anggap).
   - Gagal = jelas + sebab (bukan senyap).
   - Taip teks: Gboard auto-betulkan perkataan BM ("Produk"→"Product",
     diukur 4 Nov) → tukar keyboard SEKEJAP ke keyboard lain semasa taip,
     kemudian PULIHKAN keyboard asal (finally). Lihat taipTepat().
   ============================================================ */

export const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// Elemen ringkas: { text, desc, id (tanpa awalan pakej), cls, clickable, checked, selected, b:{x1,y1,x2,y2} }
// Parse sendiri (bukan parseUiXml) kerana perlu `selected` (cth identiti aktif penukar profile FB).
export async function bacaSkrin(ctl) {
  const xml = await ctl.dumpUi();
  const docx = new DOMParser().parseFromString(xml, 'text/xml');
  return Array.from(docx.getElementsByTagName('node')).map((n) => {
    const m = (n.getAttribute('bounds') || '').match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
    if (!m) return null;
    return {
      text: n.getAttribute('text') || '', desc: n.getAttribute('content-desc') || '',
      id: String(n.getAttribute('resource-id') || '').replace(/^.*:id\//, ''),
      cls: (n.getAttribute('class') || '').split('.').pop(),
      clickable: n.getAttribute('clickable') === 'true',
      checked: n.getAttribute('checked') === 'true',
      selected: n.getAttribute('selected') === 'true',
      b: { x1: +m[1], y1: +m[2], x2: +m[3], y2: +m[4] },
    };
  }).filter(Boolean);
}

// Bacaan selamat: skrin beranimasi (feed video) → uiautomator gagal → pulang null.
export async function bacaSkrinSelamat(ctl) {
  try { return await bacaSkrin(ctl); } catch { return null; }
}

export const pusat = (e) => [Math.round((e.b.x1 + e.b.x2) / 2), Math.round((e.b.y1 + e.b.y2) / 2)];

// Tunggu sehingga pred(elemen, semua) padan pada skrin. Pulang { el, els } atau null.
export async function tunggu(ctl, pred, ms = 10000, sela = 700) {
  for (const hingga = Date.now() + ms; Date.now() < hingga;) {
    const els = await bacaSkrinSelamat(ctl);
    if (els) {
      const el = els.find((e) => pred(e, els));
      if (el) return { el, els };
    }
    await delay(sela);
  }
  return null;
}

// Tunggu SEHINGGA pred tidak lagi padan (cth butang Drafts hilang = skrin bertukar).
// gagalBacaKiraHilang: skrin asal SENTIASA boleh dibaca (cth skrin post TikTok), jadi
// 2 bacaan gagal berturut = sudah di skrin lain (feed video — diukur 4 Nov).
export async function tungguHilang(ctl, pred, ms = 10000, { gagalBacaKiraHilang = false } = {}) {
  let gagalBaca = 0;
  for (const hingga = Date.now() + ms; Date.now() < hingga;) {
    const els = await bacaSkrinSelamat(ctl);
    if (els && !els.some((e) => pred(e, els))) return true;
    gagalBaca = els ? 0 : gagalBaca + 1;
    if (gagalBacaKiraHilang && gagalBaca >= 2) return true;
    await delay(700);
  }
  return false;
}

export async function ketuk(ctl, el, tunggMs = 1200) {
  await ctl.tap(...pusat(el));
  await delay(tunggMs);
}

// Cari + ketuk. Tak jumpa → null (pemanggil putuskan gagal).
export async function cariKetuk(ctl, pred, ms = 8000, tunggMs = 1200) {
  const r = await tunggu(ctl, pred, ms);
  if (!r) return null;
  await ketuk(ctl, r.el, tunggMs);
  return r.el;
}

// argv = senarai (arahan mudah) ATAU rentetan tunggal (perlu paip | / petikan).
// ⚠️ Jangan guna ['sh','-c','a | b'] — argv disambung dengan ruang, petikan hilang.
export async function shell(ctl, argv) {
  return ctl.adb.subprocess.noneProtocol.spawnWaitText(argv);
}

export async function keyboardNaik(ctl) {
  try { return /mInputShown=true/.test(String(await shell(ctl, 'dumpsys input_method | grep mInputShown'))); } catch { return false; }
}

// Nama gambar terbaru dalam galeri (terbaru dahulu) — susunan sama galeri TikTok/FB
// (diukur 4 Nov; seri dalam saat sama dipecah ikut _id DESC).
export async function gambarTerbaru(ctl, n) {
  try {
    const out = String(await shell(ctl, `content query --uri content://media/external/images/media --projection _display_name --sort "date_added DESC, _id DESC" | head -${Math.max(1, n + 2)}`));
    return [...out.matchAll(/_display_name=(.+?)\s*$/gm)].map((m) => m[1].trim());
  } catch { return []; }
}

// Popup kebenaran Android / dialog sistem → robot TIDAK tekan (peraturan 22 Okt):
// berhenti dan minta manusia.
export async function dialogSistem(ctl) {
  try {
    const f = String(await ctl.getFocusedWindow() || '');
    return /permissioncontroller|com\.android\.systemui|GrantPermissions/i.test(f) ? f : '';
  } catch { return ''; }
}

/* Teks untuk `input text` ADB: tak boleh unicode (emoji, aksara bukan-ASCII) dan
   aksara shell berisiko → buang/tukar. Corak sama sanitizeForInput (androidRunner,
   KEMBAR — ubah satu, semak satu lagi) tetapi had panjang ikut app (TikTok/FB > 150). */
export function teksSelamat(raw, had = 2000) {
  return String(raw == null ? '' : raw)
    .replace(/[‐-―]/g, '-')
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/[&()<>|;*\\"'`$]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, had);
}

// Bandingan longgar (abaikan ruang berganda / hujung).
export const samaTeks = (a, b) => String(a || '').replace(/\s+/g, ' ').trim() === String(b || '').replace(/\s+/g, ' ').trim();

async function keyboardSemasa(ctl) {
  try { return String(await shell(ctl, ['settings', 'get', 'secure', 'default_input_method']) || '').trim(); } catch { return ''; }
}
async function senaraiKeyboard(ctl) {
  try { return String(await shell(ctl, ['ime', 'list', '-s']) || '').split(/\s+/).filter((x) => x.includes('/')); } catch { return []; }
}

/* Taip teks TEPAT ke kotak teks.
   1. Tukar keyboard ke keyboard bukan-asal (utamakan input suara Google — tiada
      auto-betul) → key event terus ke kotak teks.
   2. BARU ketuk kotak (ketukKotak). ⚠️ Diukur 4 Nov: ketuk dahulu, tukar keyboard
      kemudian = kotak hilang sambungan, teks TIDAK masuk langsung.
   3. Ctrl+A + Delete (kosongkan teks lama).
   4. input text.
   5. PULIHKAN keyboard asal (finally — walau ralat).
   ketukKotak() = async, ketuk kotak sasaran · bacaKotak() = async, pulang teks kotak.
   Pulang { ok, teks, nota }. */
// URL untuk `input text`: TIDAK dibersihkan (& ? = mesti kekal) → dibalut petikan tunggal
// supaya shell phone tidak mentafsir & / ? (argv disambung jadi satu baris arahan).
export function urlSelamat(raw) {
  const u = String(raw || '').trim();
  return /^https?:\/\/[^\s'"`\\<>]+$/i.test(u) ? u : '';
}
async function taipMentah(ctl, teks) {
  return shell(ctl, `input text '${String(teks).replace(/'/g, "'\\''")}'`);
}

// perlahan: satu huruf satu masa (jeda 150ms). ⚠️ Kotak skrin web TikTok (cth "Rename
// product" beg kuning) MENCICIR huruf bila ditaip sekali gus: "BELI KAT SINI" → "BEL KAT II"
// (diukur 4 Nov); satu-satu huruf = tepat.
async function taipPerlahan(ctl, teks) {
  for (const ch of String(teks)) {
    await shell(ctl, ['input', 'text', ch === ' ' ? '%s' : ch]);
    await delay(150);
  }
}

export async function taipTepat(ctl, teks, { ketukKotak, bacaKotak, mentah = false, perlahan = false } = {}, log = () => {}) {
  const asal = await keyboardSemasa(ctl);
  const lain = (await senaraiKeyboard(ctl)).filter((k) => k !== asal);
  const ganti = lain.find((k) => /voice/i.test(k)) || lain[0] || '';
  let ditukar = false;
  try {
    if (ganti) {
      const out = String(await shell(ctl, ['ime', 'set', ganti]) || '');
      ditukar = /selected/i.test(out) || !/error|unknown|not/i.test(out);
      await delay(500);
    }
    if (ketukKotak) { await ketukKotak(); await delay(900); }
    await shell(ctl, ['input', 'keycombination', '113', '29']); // Ctrl+A
    await delay(250);
    await shell(ctl, ['input', 'keyevent', '67']);               // Delete
    await delay(400);
    if (teks) await (mentah ? taipMentah(ctl, teks) : perlahan ? taipPerlahan(ctl, teks) : ctl.inputText(teks));
    await delay(Math.min(6000, 800 + teks.length * 8));
  } finally {
    if (ditukar && asal) {
      try { await shell(ctl, ['ime', 'set', asal]); } catch { /* cuba sekali lagi di bawah */ }
      await delay(300);
      if ((await keyboardSemasa(ctl)) !== asal) {
        try { await shell(ctl, ['ime', 'set', asal]); } catch { log('Keyboard asal gagal dipulihkan', 'err'); }
      }
    }
  }
  const dapat = bacaKotak ? await bacaKotak() : null;
  if (dapat == null) return { ok: false, teks: '', nota: 'kotak-tak-dapat-dibaca' };
  const ok = samaTeks(dapat, teks) || (teks && samaTeks(dapat, teks + ' '));
  return { ok, teks: dapat, nota: ok ? '' : (ditukar ? 'teks-berbeza' : 'teks-berbeza-keyboard-asal') };
}
